import type { Competitor } from '../models/ProductTypes';
import { SkinSeoulProductType } from '../models/ProductTypes';

export interface MatchedCompetitorProductRequest {
  sku: string;
  possibility: number;
}

export interface MatchedCompetitorProductsRequest {
  competitor: Competitor;
  competitorProductIdentifiers: MatchedCompetitorProductRequest[];
}

export interface SkinSeoulProductIdentifierRequest {
  sku: string;
  type: SkinSeoulProductType;
  variation?: SkinSeoulProductIdentifierRequest[];
}

export interface MatchWithSkinSeoulProductRequest {
  skinSeoulProduct: SkinSeoulProductIdentifierRequest;
  machineMatchedProducts: MatchedCompetitorProductsRequest[];
  humanMatchedProducts?: MatchedCompetitorProductsRequest[];
}

export interface MatchCompetitorResponse {
  success: boolean;
}

export interface CompetitorProductRequest {
  productImageUrls: string[];
  sku: string;
  name: string;
  normalizedName: string;
  brand: string;
  category: string[];
  productLink: string;
  size?: string;
  pack?: string;
}

export interface InsertCompetitorProductRequest {
  competitor: Competitor;
  products: CompetitorProductRequest[];
}

export interface CompetitorProductSearchResult {
  competitor: Competitor;
  products: CompetitorProductRequest[];
}

export interface ProductData {
  skinSeoulProduct: any;
  machineMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
}

export class CompetitorClient {
  private readonly headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Origin: window.location.origin,
  };

  constructor(private readonly apiUrl: string) {}

  public async fetchMachineMatchedProducts(): Promise<ProductData[]> {
    try {
      const response = await fetch(`${this.apiUrl}/competitor/machineMatched`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch machine matched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching machine matched products:', error);
      throw error;
    }
  }

  public async matchCompetitorProduct(
    skinSeoulSku: string,
    humanMatchedProducts: MatchedCompetitorProductsRequest[],
    existingMachineMatchedProducts: MatchedCompetitorProductsRequest[],
  ): Promise<MatchCompetitorResponse> {
    const requestBody: MatchWithSkinSeoulProductRequest = {
      skinSeoulProduct: {
        sku: skinSeoulSku,
        type: SkinSeoulProductType.Simple,
      },
      machineMatchedProducts: existingMachineMatchedProducts || [],
      humanMatchedProducts: humanMatchedProducts || [],
    };

    try {
      const response = await fetch(`${this.apiUrl}/competitor/match`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify([requestBody]),
      });

      if (!response.ok) {
        throw new Error(`Failed to match competitor product: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error matching competitor product:', error);
      throw error;
    }
  }

  public async insertCompetitorProduct(request: InsertCompetitorProductRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/competitor`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to insert competitor product: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error inserting competitor product:', error);
      throw error;
    }
  }

  public async searchCompetitorProducts(competitor: Competitor, term: string): Promise<CompetitorProductSearchResult> {
    try {
      const response = await fetch(
        `${this.apiUrl}/competitor/products/${competitor}/search?term=${encodeURIComponent(term)}`,
        {
          method: 'GET',
          headers: this.headers,
          mode: 'cors',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to search competitor products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching competitor products:', error);
      throw error;
    }
  }
}
